import { API_BASE, api } from '../../core/network/api';

/**
 * Download an authenticated file (invoice PDFs) and hand it to the browser.
 *
 * Why not a plain `<a href="/api/...">`? Two reasons, both of which broke the
 * Settings → Invoices "PDF" link once `API_URL` started being injected at
 * runtime (`env-config.js` → `API_BASE`):
 *
 *  1. The API no longer lives on the frontend's origin, so a root-relative
 *     `/api/...` href hits the static server and 404s.
 *  2. On a genuinely cross-origin deploy the `SameSite=Strict` access cookie
 *     would not ride a cross-site top-level navigation anyway.
 *
 * A `fetch` against `API_BASE` with `credentials: 'include'` fixes both. It
 * bypasses the axios interceptor, so the 15-minute access-token expiry is
 * handled here: one 401 buys a refresh and a single retry, matching what
 * `core/network/api.ts` does for JSON calls.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const url = `${API_BASE}${path}`;
  const request = () => fetch(url, { credentials: 'include' });

  let res = await request();
  if (res.status === 401) {
    // Access cookie expired mid-session — same refresh-and-retry the axios
    // client performs, done by hand because `fetch` never sees that interceptor.
    await api.post('/auth/refresh').catch(() => null);
    res = await request();
  }
  if (!res.ok) throw new Error(`Download failed (${res.status})`);

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick — Safari aborts the download if the object URL dies
  // in the same frame as the synthetic click.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/** Download one of MY invoices (Settings → Invoices). */
export const downloadInvoice = (invoiceNumber: string) =>
  downloadFile(
    `/topup/invoices/${encodeURIComponent(invoiceNumber)}/download`,
    `${invoiceNumber}.pdf`,
  );

/** Download the XLSX bulk-import template (Admin → Invoices → Template). */
export const downloadInvoiceTemplate = () =>
  downloadFile('/admin/invoices/template', 'invoice-template.xlsx');

/** Download any invoice as an admin (Admin → Invoices). */
export const downloadInvoiceAsAdmin = (invoiceNumber: string) =>
  downloadFile(
    `/admin/invoices/${encodeURIComponent(invoiceNumber)}/download`,
    `${invoiceNumber}.pdf`,
  );
